import PropTypes from 'prop-types';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { toast } from 'react-toastify';

import patchReverseAddTypeItems from '@/common/api/type-items/patchReverseAddTypeItems';
import postTypeItems from '@/common/api/type-items/postTypeItems';
import Snack from '@/common/components/molecules/Snack/Snack';
import {
  ITEM_TYPES,
  PREDICTIONS_KEY_PARAM,
  ROLES,
  ROUTE_CARTS,
  ROUTE_ECATALOGS,
  ROUTE_NODES,
  ROUTE_PRODUCT,
  ROUTE_QUOTES,
  ROUTE_SEARCH,
  ROUTE_WISHLISTS,
  SCANBAT,
  SESSION_STORAGE_DISABLED_IA,
  TRUE_STRING,
} from '@/common/constants';
import { useMiniCartContext } from '@/common/contexts/MiniCartContext/MiniCartContext';
import useQueryParams from '@/common/hooks/useQueryParams';
import { CLOUD_RETAIL, TAG_MANAGER } from '@/ebat/constants';
import useCart, { getQueryKey } from '@/ebat/hooks/carts/useCart';
import useCarts from '@/ebat/hooks/carts/useCarts';
import { ROUTE_CART } from '@/materiaux/constants';

import { useAuth } from './AuthContext';
import { useConfig } from './ConfigContext';
import { useDefaultCartContext } from './DefaultCartContext';
import { useTagManager } from './TagManagerContext';

const AddToContext = createContext(null);

export const AddToContextProvider = ({ children, isLoggedIn, isScanBat }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { onPushCloudRetail } = useTagManager();
  const getQueryParams = useQueryParams();
  const { isMateriaux } = useConfig();
  const { user } = useAuth();
  const { setIsVisible, resetTimeout } = useMiniCartContext();
  const location = useLocation();
  const isCartPage = location.pathname.includes(ROUTE_CART);
  const isEcatalogPage = location.pathname.includes(ROUTE_ECATALOGS);
  const isProductPage = location.pathname.includes(ROUTE_PRODUCT);
  const isProductsPage = location.pathname.includes(ROUTE_NODES);
  const isSearchPage = location.pathname.includes(ROUTE_SEARCH);

  // Get default cart context t get the refetchDefaultCart callback to refresh quantity in header after action on cart item
  const { refetchDefaultCart, defaultCart } = useDefaultCartContext();
  const queryClient = useQueryClient();

  const { data: carts } = useCarts({
    isLoggedIn: !!user?.user,
    sources: isScanBat ? ['scanbat'] : null,
  });

  const { refetch: refetchCart } = useCart(
    isScanBat
      ? { id: carts?.data[0]?.id, setDefault: false, disabled: 'pouet' }
      : {
          id: defaultCart?.id,
          setDefault: true,
          disabled: !user?.role || user?.role === ROLES.CUSTOMER_PRESENTER,
        },
  );

  // To  construct route dynamically
  const routes = useMemo(
    () => ({
      ROUTE_CARTS,
      ROUTE_QUOTES,
      ROUTE_WISHLISTS,
    }),
    [],
  );

  // Default AddTo options
  const defaultOptions = useMemo(
    () => ({
      flashMessage: true,
      modal: false,
      undo: true,
      link: true,
      linkUrl: null,
      linkAction: null,
      predictions: false,
    }),
    [],
  );

  const generateCartSignature = (cart) => {
    if (!cart) {
      return undefined;
    }
    return `nb-items-${cart?.nb_items}-sum-quantity-${cart?.sum_quantity}`;
  };

  const previousCartSignature = useRef(generateCartSignature(defaultCart));

  useEffect(() => {
    const currentSignature = generateCartSignature(defaultCart);
    if (!isMateriaux) {
      if (
        previousCartSignature.current !== currentSignature &&
        !isCartPage &&
        !isEcatalogPage &&
        (isProductPage || isProductsPage
          ? sessionStorage.getItem(SESSION_STORAGE_DISABLED_IA) === TRUE_STRING
          : true) &&
        (isSearchPage
          ? sessionStorage.getItem(SESSION_STORAGE_DISABLED_IA) === TRUE_STRING
          : true)
      ) {
        setIsVisible(true);
        resetTimeout();
      }

      if (previousCartSignature.current === undefined) {
        setIsVisible(false);
      }

      previousCartSignature.current = currentSignature;
    }
  }, [
    defaultCart,
    isCartPage,
    isEcatalogPage,
    isProductPage,
    isProductsPage,
    isSearchPage,
    setIsVisible,
    resetTimeout,
    isMateriaux,
  ]);

  /**
   * Trigger refresh type after action (add, cancel ...)
   * @type {boolean}
   */
  // CAUTION: ******************************************
  // this attribute is set to false because of multiple call to AS400
  // so refreshCart Methode is not use at this moment.
  const triggerRefreshCart = false;
  // TODO: set to true or false depends on AS400 interaction
  // eslint-disable-next-line no-unused-vars
  const triggerRefreshQuote = false;
  // TODO: set to true or false depends on AS400 interaction
  // eslint-disable-next-line no-unused-vars
  const triggerRefreshWishlist = false;

  /**
   * Push dataLayer
   * @param {object} cart
   * @param {array} items
   * @param {object} options
   * @type {function(): void}
   */
  const handlePushDataLayer = useCallback(
    ({ cart, items, options }) => {
      const tagManagerArgs = {
        event: TAG_MANAGER.EVENTS.CTA_ADD_TO_CART,
        origin: options?.dataLayer?.origin || TAG_MANAGER.ORIGINS.UNDEFINED,
        cloud_retail: {
          eventType: CLOUD_RETAIL.EVENTS.CTA_ADD_TO_CART,
          attributionToken: getQueryParams.get(PREDICTIONS_KEY_PARAM),
          cartId: cart.id.toString(),
          productDetails: items.reduce((acc, item) => {
            acc.push({
              product: {
                id: item.sku,
              },
              quantity: item.quantity,
            });

            return acc;
          }, []),
        },
      };

      if (items.length === 1) {
        tagManagerArgs.product = {
          id: items[0].sku,
          brand: items[0].brand?.name,
          name: items[0].name,
        };

        // Test if brand is null
        if (!items[0].brand) {
          delete tagManagerArgs.product.brand;
        }
      }

      // Token for products in prediction
      if (!getQueryParams.get(PREDICTIONS_KEY_PARAM)) {
        delete tagManagerArgs.cloud_retail.attributionToken;
      }

      onPushCloudRetail(tagManagerArgs);
    },
    [getQueryParams, onPushCloudRetail],
  );

  /**
   * Redirect to target page
   * @param {object} target
   * @param {string} type (cart|quotes|wishlists)
   */
  const redirectToType = useCallback(
    ({ target, type }) =>
      history.push(`${routes[`ROUTE_${type.toUpperCase()}`]}/${target.id}`),
    [routes, history],
  );

  /**
   * Undo add to type
   * Get mutator and set the mutation api
   */
  const {
    mutate: mutatePatchReverseAddTypeItems,
    isLoading: isLoadingPatchReverseAddItems,
    isSuccess: isSuccessPatchReverseAddItems,
  } = useMutation(patchReverseAddTypeItems);

  /**
   * Undo callback sent to flashMessage to call an undo request with the same data as add
   * @param {object} target
   * @param {array} items array of items to add to target
   * @param {string} type of target (cart|quotes|wishlists)
   */
  const reverseAddTypeItems = useCallback(
    ({ type, items, target }) => {
      mutatePatchReverseAddTypeItems(
        {
          target,
          items: items.reduce((acc, item) => {
            const quote = {};
            if (item.quote_number) {
              quote.quote_number = item.quote_number;
              quote.quote_sequence = item.quote_sequence;
              quote.quote_line = item.quote_line;
            }
            acc.push({ quantity: item.quantity, sku: item.sku, ...quote });
            return acc;
          }, []),
          type,
        },
        {
          onSuccess: () => {
            refetchCart();
            // Refresh header to display the new quantity
            if (type === ITEM_TYPES.carts) {
              // TODO:  depends on type
              refetchDefaultCart();
            }
            // Refresh cart cache to get items instantly in cart page if triggerRefreshCart
            // TODO: Refresh cart cache to get items instantly in cart page if triggerRefreshCart
            if (triggerRefreshCart) {
              //refreshCart();
            }
            // Inform the user with a flashMessage
            toast.success(
              <Snack message={t('common:messages:forms.success.undo')} />,
            );
          },
          onError: (error) => {
            // In case of error
            if (error.response.status === 500) {
              toast.error(
                <Snack
                  message={t('common:messages:forms.errors.server_error')}
                />,
              );
            } else {
              toast.error(
                <Snack message={t('common:messages:forms.errors.default')} />,
              );
            }
          },
        },
      );
    },
    [
      mutatePatchReverseAddTypeItems,
      refetchCart,
      refetchDefaultCart,
      t,
      triggerRefreshCart,
    ],
  );

  /**
   * Add items to target
   * Get mutator and set the mutation api
   */
  const {
    mutate: mutatePostItems,
    isLoading: isLoadingAddItems,
    isSuccess: isSuccessAddItems,
  } = useMutation(postTypeItems);

  /**
   * Callback given to the children component to add items(s) in a specified type, if exist
   * @param {object} target
   * @param {array} items array of items to add to target
   * @param {string} type of target (cart|quotes|wishlists)
   * @param {function} onSuccessCallback callback executed after success
   * @param {object} options (display flashMessage, messages, modal ...)
   */
  const addTo = useCallback(
    ({ type, items, target, onSuccessCallback, options }) => {
      if (isLoggedIn) {
        mutatePostItems(
          {
            target,
            items,
            type,
          },
          {
            onSuccess: ({ data, headers }) => {
              // Refresh header to display the new quantity
              if (type === ITEM_TYPES.carts) {
                // TODO:  depends on type
                refetchDefaultCart();
              }
              // TODO: Refresh cart cache to get items instantly in cart page if triggerRefreshCart
              if (triggerRefreshCart) {
                //refreshCart();
              }
              if (options.flashMessage) {
                // Inform the user that a/some items(s) have been added to the target name with a flashMessage.
                // This flash message contains an undo CTA (see undoAddItemToTarget)
                // and a redirect CTA to the type page

                const snackProps = {
                  actionTextDisp: t(options.messages.link), // plural
                  onAction: options.linkAction || options.linkUrl,
                  undoTextDisp: t(options.messages.undo),
                  onUndo: () => reverseAddTypeItems({ type, items, target }),
                  message: t(options.messages.text, {
                    name: isMateriaux ? null : target.name,
                  }),
                };
                // Remove undo from Snack
                if (!options.undo) {
                  delete snackProps.onUndo;
                  delete snackProps.undoTextDisp;
                }
                // Remove link from Snack
                if (!options.link) {
                  delete snackProps.onAction;
                  delete snackProps.actionTextDisp;
                }

                toast.success(
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  <Snack {...snackProps} />,
                  { autoClose: target?.name === SCANBAT ? 10000 : 5000 },
                );
              }
              // execute onSuccessCallback callback given
              // Installer quote need update totals extracted from http response headers
              onSuccessCallback(
                type === ITEM_TYPES.installer ? { items: data, headers } : data,
              );
            },
            onError: (error) => {
              // In case of error
              if (error.response.status === 500) {
                toast.error(
                  <Snack
                    message={t('common:messages:forms.errors.server_error')}
                  />,
                );
              } else {
                toast.error(
                  <Snack message={t('common:messages:forms.errors.default')} />,
                );
              }
            },
          },
        );
      } else {
        toast.error(
          <Snack message={t('common:messages:forms.errors.not_logged')} />,
        );
      }
    },
    [
      isLoggedIn,
      isMateriaux,
      mutatePostItems,
      refetchDefaultCart,
      reverseAddTypeItems,
      t,
      triggerRefreshCart,
    ],
  );

  /**
   * Add items collection to the given cart
   * This method calls addTo with the right signature
   * @param {object} cart {id: int, name: string}
   * @param {array} items to add to targeted cart
   * @param {function} onSuccessCallback callback on success
   * @param {object} options (display flashMessage, messages, modal ...)
   */
  const addToCart = useCallback(
    ({ cart, items, onSuccessCallback = () => {}, options }) => {
      const addToCartOptions = {
        ...defaultOptions,
        ...options,
        linkUrl:
          options &&
          (options.linkUrl
            ? () => history.push(options.linkUrl)
            : () =>
                isMateriaux
                  ? (() => {
                      history.push(ROUTE_CARTS);
                    })()
                  : redirectToType({ target: cart, type: ITEM_TYPES.carts })),
        messages: {
          title: options?.messages?.title || '',
          text:
            options?.messages?.text || 'common:messages:forms.success.added',
          link:
            options?.messages?.link || 'common:messages:actions.cart.manage',
        },
        undo: false,
      };

      addTo({
        target: { ...cart },
        items: items.reduce((acc, item) => {
          let quote = {};
          if (item.quote_line) {
            const variantId = item.variant_id ?? null;
            const variantLine = item.variant_line ?? null;
            const optionId = item.option_id ?? null;
            const optionLine = item.option_line ?? null;
            quote = {
              quote_line: item.quote_line,
              quote_number: item.quote_number,
              quote_sequence: item.quote_sequence,
            };
            if (variantId !== null) {
              quote.variant_id = variantId;
            }
            if (variantLine !== null) {
              quote.variant_line = variantLine;
            }
            if (optionId !== null) {
              quote.option_id = optionId;
            }
            if (optionLine !== null) {
              quote.option_line = optionLine;
            }
          }

          acc.push({
            ...quote,
            quantity: item.quantity,
            sku: item.sku,
            source: item.source,
          });

          return acc;
        }, []),
        type: ITEM_TYPES.carts,
        onSuccessCallback: (data) => {
          queryClient.cancelQueries(getQueryKey(cart.id, true));
          refetchCart();
          onSuccessCallback(data);
          handlePushDataLayer({
            cart,
            items,
            options,
          });
        },
        options: { ...addToCartOptions },
      });
    },
    [
      addTo,
      defaultOptions,
      handlePushDataLayer,
      history,
      isMateriaux,
      queryClient,
      redirectToType,
      refetchCart,
    ],
  );

  /**
   * Add items collection to the given quote
   * This method calls addTo with the right signature
   * @param {object} quote {id: int, name: string}
   * @param {array} items to add to targeted quote
   * @param {function} onSuccessCallback callback on success
   * @param {object} options (display flashMessage, messages, modal ...)
   */
  const addToQuote = useCallback(
    ({
      quote,
      items,
      onSuccessCallback = () => {},
      options = defaultOptions,
    }) =>
      addTo({
        target: { ...quote },
        items,
        type: ITEM_TYPES.quotes,
        onSuccessCallback,
        options,
      }),
    [addTo, defaultOptions],
  );

  /**
   * Add items collection to the given quote installer
   * This method calls addTo with the right signature
   * @param {object} installer quote {id: int, name: string}
   * @param {array} items to add to targeted installer quote
   * @param {function} onSuccessCallback callback on success
   * @param {object} options (display flashMessage, messages, modal ...)
   */
  const addToInstallerQuote = useCallback(
    ({
      installer,
      items,
      onSuccessCallback = () => {},
      options = defaultOptions,
    }) => {
      addTo({
        target: { ...installer },
        items,
        type: ITEM_TYPES.installer,
        onSuccessCallback: (data) => {
          onSuccessCallback(data);
        },
        options,
      });
    },
    [addTo, defaultOptions],
  );

  /**
   * Add items collection to the given quote draft
   * This method calls addTo with the right signature
   * @param {object} draft quote {id: int, name: string}
   * @param {array} items to add to targeted draft quote
   * @param {function} onSuccessCallback callback on success
   * @param {object} options (display flashMessage, messages, modal ...)
   */
  const addToDraftQuote = useCallback(
    ({
      draft,
      items,
      onSuccessCallback = () => {},
      options = defaultOptions,
    }) => {
      addTo({
        target: { ...draft },
        items,
        type: ITEM_TYPES.draft,
        onSuccessCallback,
        options,
      });
    },
    [addTo, defaultOptions],
  );

  /**
   * Add items collection to the given wishlists
   * This method calls addTo with the right signature
   * @param {object} wishlist {id: int, name: string}
   * @param {array} items to add to targeted wishlist
   * @param {function} onSuccessCallback callback on success
   * @param {object} options (display flashMessage, messages, modal ...)
   */
  const addToWishlist = useCallback(
    ({ wishlist, items, onSuccessCallback = () => {}, options }) => {
      const addToWishlistOptions = {
        ...defaultOptions,
        ...options,
        linkUrl:
          options &&
          (options.linkUrl
            ? () => history.push(options.linkUrl)
            : () =>
                redirectToType({
                  target: wishlist,
                  type: ITEM_TYPES.wishlists,
                })),
        messages: {
          title: options?.messages?.title || '',
          text:
            options?.messages?.text ||
            'common:messages:forms.success.add_wishlist',
          undo: options?.messages?.undo || 'common:messages:actions.cancel',
          link:
            options?.messages?.link ||
            'common:messages:actions.wishlist.manage',
        },
      };
      addTo({
        target: { ...wishlist },
        items,
        type: ITEM_TYPES.wishlists,
        onSuccessCallback: (data) => {
          onSuccessCallback(data);
        },
        options: { ...addToWishlistOptions },
      });
    },
    [addTo, defaultOptions, history, redirectToType],
  );

  /**
   * Call the default type to update the react-query type cache
   * @param {number} id type id
   */
  // TODO: for cart, to refresh cash
  // eslint-disable-next-line no-unused-vars
  const refresh = useCallback(() => {}, []);

  return (
    <AddToContext.Provider
      value={{
        addToCart,
        addToDraftQuote,
        addToInstallerQuote,
        addToQuote,
        addToWishlist,
        isLoading: isLoadingAddItems || isLoadingPatchReverseAddItems,
        isSuccess: isSuccessAddItems || isSuccessPatchReverseAddItems,
      }}
    >
      {children}
    </AddToContext.Provider>
  );
};

AddToContextProvider.defaultProps = {
  isLoggedIn: false,
  isScanBat: false,
};

AddToContextProvider.propTypes = {
  isLoggedIn: PropTypes.bool,
  isScanBat: PropTypes.bool,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

export const useAddToContext = () => useContext(AddToContext);
